import * as i0 from '@angular/core';
import { Directive, Input, Injectable, Component, ChangeDetectionStrategy, HostBinding, EventEmitter, ElementRef, Output, ContentChild, ViewChild, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i5 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import * as i4 from '@angular/material/checkbox';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import * as i2$1 from '@angular/material/tree';
import { MatTreeFlattener, MatTreeFlatDataSource, MatTreeModule } from '@angular/material/tree';
import * as i1 from '@firestitch/menu';
import { FsMenuModule } from '@firestitch/menu';
import { BehaviorSubject, Subject, timer } from 'rxjs';
import { takeUntil, filter, map } from 'rxjs/operators';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';

class FsTreeNodeDirective {
    constructor(_templateRef) {
        this._templateRef = _templateRef;
        this._klass = '';
    }
    get templateRef() {
        return this._templateRef;
    }
    get klass() {
        return this._klass;
    }
}
FsTreeNodeDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeNodeDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
FsTreeNodeDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.16", type: FsTreeNodeDirective, selector: "[fsTreeNode]", inputs: { _klass: ["class", "_klass"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeNodeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsTreeNode]'
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; }, propDecorators: { _klass: [{
                type: Input,
                args: ['class']
            }] } });

class LoggerService {
    constructor() {
        this._enabled = false;
    }
    set enabled(val) {
        this._enabled = val;
    }
    log(type, message) {
        if (this._enabled) {
            console.warn(`Type: ${type}`, message);
        }
    }
    profileStart(label) {
        if (this._enabled) {
            this.log('PROFILE', 'Started - ' + label);
            // console.profile(label);
        }
    }
    profileStop(label) {
        if (this._enabled) {
            this.log('PROFILE', 'Finished - ' + label);
            // console.profileEnd(label);
        }
    }
    timeStart(label) {
        if (this._enabled) {
            this.log('TIMER', 'Started - ' + label);
            console.time(label);
        }
    }
    timeStop(label) {
        if (this._enabled) {
            this.log('TIMER', 'Finished - ' + label);
            console.timeEnd(label);
        }
    }
}
LoggerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: LoggerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
LoggerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: LoggerService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: LoggerService, decorators: [{
            type: Injectable
        }] });

class ItemNode {
    constructor(data = {}) {
        this.children = data.children || null;
        this.data = data.data || null;
        this.parent = data.parent || null;
    }
}

/**
 * Build the tree structure. The `value` is the Json object, or a sub-tree of a Json object.
 * The return value is the list of `treeBuilder`.
 */
function treeBuilder(target, level = 0, parent = null, childrenName = null, maxLevel = Infinity) {
    // Limit
    if (level > maxLevel) {
        return;
    }
    const isArray = Array.isArray(target);
    if (!parent && isArray) {
        return target.reduce((acc, item) => {
            const node = treeBuilder(item, level, parent, childrenName, maxLevel);
            if (node) {
                acc.push(node);
            }
            return acc;
        }, []);
    }
    else if (parent && isArray) {
        target.forEach((item) => {
            if (!parent.children) {
                parent.children = [];
            }
            const node = treeBuilder(item, level + 1, parent ? parent : [], childrenName, maxLevel);
            if (node) {
                parent.children.push(node);
            }
        });
    }
    else if (typeof target === 'object') {
        const node = new ItemNode({
            data: target,
            parent: parent
        });
        if (target[childrenName]) {
            treeBuilder(target[childrenName], level + 1, node, childrenName, maxLevel);
        }
        return node;
    }
}

/**
 * Build the tree structure. The `value` is the Json object, or a sub-tree of a Json object.
 * The return value is the list of `treeBuilder`.
 */
function treeSort(target, sortFn) {
    if (!sortFn) {
        return target;
    }
    const isArray = Array.isArray(target);
    if (isArray) {
        target.forEach((item) => {
            if (item.children) {
                item.children = treeSort(item.children, sortFn);
            }
        });
        return sortDataBy(target, sortFn);
    }
}
function sortDataBy(data, sortFn, parent) {
    if (!sortFn) {
        return data;
    }
    const _cachedNodes = new Map();
    const cleanData = data.map((node) => {
        _cachedNodes.set(node.data, node);
        return node.data;
    });
    if (!parent && data.length > 0) {
        parent = data[0].parent && data[0].parent.data;
    }
    else if (parent) {
        parent = parent.data;
    }
    const sortedData = sortFn(cleanData, parent);
    const sortedNodes = sortedData.reduce((acc, item) => {
        const node = _cachedNodes.get(item);
        node.data = item;
        acc.push(node);
        return acc;
    }, []);
    _cachedNodes.clear();
    return sortedNodes;
}

class FlatItemNode {
    constructor(data = {}) {
        this.hidden = false; // Need for droppable orderNodesByCoords
        this.canDrag = true;
        this.canNodeClick = true;
        this.templateContext = {};
        this._lastNode = false;
        this._firstNode = false;
        this._index = null;
        this._dataStoredKeys = [];
        this.data = data.data || null;
        this.level = data.level;
        this.expandable = data.expandable || false;
        this.parent = data.parent || null;
        this.original = data.original || null;
        this.originalParent = data.originalParent || null;
        this.isExpanded = data.isExpanded || function () {
            return false;
        };
        this.collapse = data.collapse || function () { };
        this.expand = data.expand || function () { };
        this.canDrag = data.canDrag === void 0 ? true : data.canDrag;
        this.canNodeClick = data.canNodeClick === void 0 ? true : data.canNodeClick;
        this.hidden = this.isExpanded();
    }
    get data() {
        return this._data;
    }
    set data(value) {
        this._data = value;
        if (this.original) {
            this.original.data = value;
        }
        this._updateContext();
    }
    get level() {
        return this._level;
    }
    set level(value) {
        this._level = value;
        this.templateContext.level = this.level;
    }
    get parent() {
        return this._parent;
    }
    set parent(value) {
        this._parent = value;
        this.templateContext.parent = this.parent;
    }
    set index(value) {
        this._index = value;
    }
    get index() {
        return this._index;
    }
    set first(value) {
        this._firstNode = value;
    }
    set last(value) {
        this._lastNode = value;
    }
    /**
     * Do update for template Context
     */
    _updateContext() {
        // Remove previously stored data keys (deduplicate)
        this._dataStoredKeys.forEach((key) => {
            delete this.templateContext[key];
        });
        delete this.templateContext.node;
        // Store new data keys
        if (this.data) {
            this._dataStoredKeys = Object.keys(this.data);
        }
        for (const key in this.data) {
            const item = this.data[key];
            if (this.data.hasOwnProperty(key)) {
                this.templateContext[key] = item;
            }
        }
        this.templateContext.level = this.level;
        this.templateContext.parent = this.parent;
        this.templateContext.data = this._data;
        this.templateContext.node = this._data;
        this.templateContext.flatItemNode = this;
        this.templateContext.index = this._index;
        this.templateContext.last = this._lastNode;
        this.templateContext.first = this._firstNode;
    }
}

class FsTreeDatabaseService {
    constructor() {
        /** Map from flat node to nested node. This helps us finding the nested node to be modified */
        this.flatNodeMap = new Map();
        /** Map from nested node to flattened node. This helps us to keep the same object for selection */
        this.nestedNodeMap = new Map();
        this._data$ = new BehaviorSubject([]);
        this._dataChange = new Subject();
        this._destroy$ = new Subject();
    }
    get dataChange() {
        return this._dataChange.asObservable();
    }
    get data() {
        return this._data$.getValue();
    }
    set data(value) {
        this._data$.next(value);
    }
    get data$() {
        return this._data$.asObservable();
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    initialize(treeControl, config) {
        this.treeControl = treeControl;
        this._config = config;
        // Build the tree nodes from Json object. The result is a list of `ItemNode` with nested
        // file node as children.
        this.data = treeBuilder(config.data, 0, null, config.childrenName, config.maxLevel);
        this.data = treeSort(this.data, config.sortBy);
        // Notify the change.
        this.updateData("init" /* Init */, this.data);
    }
    setData(data) {
        this.data = treeBuilder(data, 0, null, this._config.childrenName, this._config.maxLevel);
        this.data = treeSort(this.data, this._config.sortBy);
        // Notify the change.
        this.updateData("update_data" /* UpdateData */, this.data);
    }
    updateSort(target = null) {
        if (target && target.children) {
            target.children = treeSort(target.children, this._config.sortBy);
        }
        else {
            this.data = treeSort(this.data, this._config.sortBy);
        }
    }
    // Create new fresh node which is ready to be insterted into tree
    createNode(data, parent = null) {
        const forLevel = parent ? parent.level + 1 : 0;
        const node = treeBuilder(data, forLevel, parent, this._config.childrenName);
        return new FlatItemNode({
            data,
            original: node,
            parent,
            originalParent: parent ? parent.original : null,
            level: parent ? parent.level + 1 : 0,
        });
    }
    insertNodeAbove(target, node) {
        const parent = target.parent;
        this.removeItem(node);
        let insertedIndex = null;
        if (parent) {
            const targetIndex = parent.children.indexOf(target);
            parent.children.splice(targetIndex, 0, node);
            parent.children = sortDataBy(parent.children, this._config.sortBy, parent);
            insertedIndex = parent.children.indexOf(node);
        }
        else {
            const targetIndex = this.data.indexOf(target);
            this.data.splice(targetIndex, 0, node);
            this.data = sortDataBy(this.data, this._config.sortBy);
            insertedIndex = this.data.indexOf(node);
        }
        node.parent = target.parent;
        return insertedIndex;
    }
    insertNodeBelow(target, node) {
        const parent = target.parent;
        this.removeItem(node);
        let insertedIndex = null;
        if (parent) {
            const targetIndex = parent.children.indexOf(target);
            parent.children.splice(targetIndex + 1, 0, node);
            parent.children = sortDataBy(parent.children, this._config.sortBy, parent);
            insertedIndex = parent.children.indexOf(node);
        }
        else {
            const targetIndex = this.data.indexOf(target);
            this.data.splice(targetIndex + 1, 0, node);
            this.data = sortDataBy(this.data, this._config.sortBy);
            insertedIndex = this.data.indexOf(node);
        }
        node.parent = target.parent;
        return insertedIndex;
    }
    insertNode(target, node) {
        this.removeItem(node);
        let insertedIndex = null;
        if (target) {
            if (!target.children) {
                target.children = [];
            }
            target.children.push(node);
            node.parent = target;
            target.children = sortDataBy(target.children, this._config.sortBy, target);
            insertedIndex = target.children.indexOf(node);
        }
        else {
            this.data.push(node);
            this.data = sortDataBy(this.data, this._config.sortBy);
            insertedIndex = this.data.indexOf(node);
        }
        return insertedIndex;
    }
    removeItem(node) {
        const parent = node.parent;
        if (parent && parent.children) {
            const nodeIndex = parent.children.indexOf(node);
            if (nodeIndex > -1) {
                parent.children.splice(nodeIndex, 1);
            }
        }
        else {
            const nodeIndex = this.data.indexOf(node);
            if (nodeIndex > -1) {
                this.data.splice(nodeIndex, 1);
            }
        }
    }
    updateData(type, payload) {
        this._dataChange.next({
            type,
            payload,
        });
    }
}
FsTreeDatabaseService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeDatabaseService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FsTreeDatabaseService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeDatabaseService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeDatabaseService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

var TreeDragAxis;
(function (TreeDragAxis) {
    TreeDragAxis["Y"] = "y";
    TreeDragAxis["XY"] = "xy";
})(TreeDragAxis || (TreeDragAxis = {}));

/**
 * Convert tree to simple data object
 */
function dataBuilder(tree, childrenName, data = []) {
    tree.forEach((node) => {
        const nodeData = node.data;
        if (node.children && node.children.length > 0) {
            nodeData[childrenName] = dataBuilder(node.children, childrenName);
        }
        data.push(nodeData);
    });
    return data;
}

function getChildren(node) {
    return node.children;
}

function getLevel(node) {
    return node.level;
}

function isExpandable(node) {
    return node.expandable;
}

class FsTreeService {
    constructor(_database, _cd, _zone) {
        this._database = _database;
        this._cd = _cd;
        this._zone = _zone;
        this.config = {};
        // Nodes can be dragged&dropped. Draggable flag
        this.reorder = true;
        // Possibility to expand/collapse for nodes
        this.blocked = false;
        /** The selection for checklist */
        this.checklistSelection = new SelectionModel(true /* multiple */);
        this._updateClasses$ = new Subject();
        this._destroy$ = new Subject();
        /**
         * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
         */
        this.transformer = (node, level) => {
            const existingNode = this._database.nestedNodeMap.get(node);
            const flatNode = existingNode && existingNode.data === node.data
                ? existingNode
                : new FlatItemNode();
            flatNode.original = node;
            flatNode.parent = this.getFlatItemNode(node.parent);
            flatNode.originalParent = this.getItemNode(flatNode.parent);
            flatNode.level = level;
            flatNode.expandable = (node.children && node.children.length > 0);
            flatNode.isExpanded = () => this.treeControl.isExpanded(flatNode);
            flatNode.collapse = () => this.treeControl.collapse(flatNode);
            flatNode.expand = () => this.treeControl.expand(flatNode);
            flatNode.canDrag = this.config.canDrag ? this.config.canDrag(flatNode) : true;
            const nodesList = level === 0
                ? this._database.data
                : flatNode.originalParent.children;
            flatNode.index = level === 0
                ? nodesList.indexOf(node)
                : nodesList.indexOf(node);
            flatNode.first = nodesList.indexOf(node) === 0;
            flatNode.last = nodesList.length - 1 === flatNode.index;
            flatNode.data = node.data;
            flatNode.canNodeClick = this.config.canNodeClick ? this.config.canNodeClick(flatNode) : false;
            this._database.flatNodeMap.set(flatNode, node);
            this._database.nestedNodeMap.set(node, flatNode);
            return flatNode;
        };
        this.treeFlattener = new MatTreeFlattener(this.transformer, getLevel, isExpandable, getChildren);
        this.treeControl = new FlatTreeControl(getLevel, isExpandable);
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    }
    get updateClasses$() {
        return this._updateClasses$.asObservable();
    }
    get dataChange$() {
        return this._database.data$;
    }
    init(el, config) {
        var _a, _b;
        this._subscribeToDataChange();
        this._database.containerElement = el;
        this.config = Object.assign(Object.assign({}, config), { draggable: (_a = config.draggable) !== null && _a !== void 0 ? _a : true, dragAxis: (_b = config.dragAxis) !== null && _b !== void 0 ? _b : TreeDragAxis.XY });
        this._database.initialize(this.treeControl, this.config);
        this._updateSelected();
        this._updateExpanded();
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    getFlatItemNode(node) {
        return this._database.nestedNodeMap.get(node);
    }
    getItemNode(node) {
        return this._database.flatNodeMap.get(node);
    }
    /** Whether all the descendants of the node are selected */
    descendantsAllSelected(node) {
        const descendants = this.treeControl.getDescendants(node);
        return descendants.every((child) => this.checklistSelection.isSelected(child));
    }
    /** Whether part of the descendants are selected */
    descendantsPartiallySelected(node) {
        const descendants = this.treeControl.getDescendants(node);
        const result = descendants
            .some((child) => this.checklistSelection.isSelected(child));
        return result && !this.descendantsAllSelected(node);
    }
    /** Toggle the to-do item selection. Select/deselect all the descendants node */
    todoItemSelectionToggle(node) {
        const nodeSelected = this.checklistSelection.isSelected(node);
        this.checklistSelection.toggle(node);
        if (nodeSelected) {
            this._deselectNode(node);
        }
        else {
            this._selectNode(node);
        }
        this._emitSelectionChange();
    }
    /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
    todoLeafItemSelectionToggle(node) {
        this.checklistSelection.toggle(node);
        this.checkAllParentsSelection(node);
        this._emitSelectionChange();
    }
    /** Checks all the parents when a leaf node is selected/unselected **/
    checkAllParentsSelection(node) {
        let parent = this.getParentNode(node);
        while (parent) {
            this.checkRootNodeSelection(parent);
            parent = this.getParentNode(parent);
        }
    }
    /** Get the parent node of a node **/
    getParentNode(node) {
        const currentLevel = getLevel(node);
        if (currentLevel < 1) {
            return null;
        }
        const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
        for (let i = startIndex; i >= 0; i--) {
            const currentNode = this.treeControl.dataNodes[i];
            if (getLevel(currentNode) < currentLevel) {
                return currentNode;
            }
        }
        return null;
    }
    /** Check root node checked state and change it accordingly */
    checkRootNodeSelection(node) {
        const nodeSelected = this.checklistSelection.isSelected(node);
        const descendants = this.treeControl.getDescendants(node);
        const descAllSelected = descendants.every((child) => this.checklistSelection.isSelected(child));
        if (nodeSelected && !descAllSelected) {
            this.checklistSelection.deselect(node);
        }
        else if (!nodeSelected && descAllSelected) {
            this.checklistSelection.select(node);
        }
    }
    /**
     * Setup drag
     *
     * @param node
     */
    onDragStart(node) { }
    onDrop(data) {
        if (data.dropInto === data.node) {
            return;
        }
        const dropInto = this._database.flatNodeMap.get(data.dropInto);
        const node = this._database.flatNodeMap.get(data.node);
        const fromParent = node.parent;
        let insertIndex = null;
        if (data.dropPosition === 'above') {
            insertIndex = this._database.insertNodeAbove(dropInto, node);
        }
        else if (data.dropPosition === 'below') {
            insertIndex = this._database.insertNodeBelow(dropInto, node);
        }
        else {
            insertIndex = this._database.insertNode(dropInto, node);
        }
        // Run in zone back because before it was ran outside angular
        this._zone.run(() => {
            // Notify about data change
            const payload = {
                fromParent: this.getFlatItemNode(fromParent),
                toParent: this.getFlatItemNode(node.parent),
                node: data.node,
                index: insertIndex,
            };
            this._database.updateData("reorder" /* Reorder */, payload);
        });
    }
    nodeClick(node) {
        if (this.config.nodeClick) {
            this.config.nodeClick({ node });
        }
    }
    /**
     * Custom method for expand/collapse, because we must check blocked flag before
     *
     * @param node
     */
    toggleNode(node) {
        if (!this.blocked) {
            if (this.treeControl.isExpanded(node)) {
                this.treeControl.collapse(node);
            }
            else {
                this.treeControl.expand(node);
            }
        }
    }
    /**
     * Transform tree to object
     */
    getData() {
        return dataBuilder(this.dataSource.data, this.config.childrenName);
    }
    setData(data) {
        this.checklistSelection.clear();
        this._database.setData(data);
        this._updateSelected();
        this._updateExpanded();
    }
    /**
     * Collapse nodes
     */
    collapseAll() {
        this.treeControl.collapseAll();
    }
    /**
     * Expand nodes
     */
    expandAll() {
        this.treeControl.expandAll();
    }
    /**
     * Enable drag&drop
     */
    enableReorder() {
        this.reorder = true;
        this._cd.markForCheck();
    }
    /**
     * Disable drag&drop
     */
    disableReorder() {
        this.reorder = false;
        this._cd.markForCheck();
    }
    /**
     * Insert element above target
     *
     * @param data
     * @param target
     */
    insertNodeAbove(data = {}, target = null) {
        const originalParent = target && target.original || null;
        const node = this._database.createNode(data, target);
        const insertIndex = this._database.insertNodeAbove(originalParent, node.original);
        // Notify about data change
        const payload = {
            position: 'above',
            parent: target,
            node,
            index: insertIndex,
        };
        this._database.updateData("insert" /* Insert */, payload);
        return node;
    }
    /**
     * Insert element below target
     *
     * @param data
     * @param target
     */
    insertNodeBelow(data = {}, target = null) {
        const originalParent = target && target.original || null;
        const node = this._database.createNode(data, target);
        const insertIndex = this._database.insertNodeBelow(originalParent, node.original);
        // Notify about data change
        const payload = {
            position: 'below',
            parent: target,
            node,
            index: insertIndex,
        };
        this._database.updateData("insert" /* Insert */, payload);
        return node;
    }
    /**
     * Insert element as child element for target node
     *
     * @param data
     * @param parent
     */
    appendNode(data = {}, parent = null) {
        const originalParent = parent && parent.original || null;
        const node = this._database.createNode(data, parent);
        this._database.insertNode(originalParent, node.original);
        if (parent && !parent.isExpanded()) {
            parent.expand();
        }
        // Notify about data change
        const payload = {
            position: 'into',
            parent,
            node,
            index: 0,
        };
        this._database.updateData("insert" /* Insert */, payload);
        return node;
    }
    /**
     * Update internal data for target
     *
     * @param data
     * @param target
     */
    updateNodeData(data = {}, target) {
        target.data = data;
        // Notify about data change
        const payload = {
            node: target,
        };
        this._database.updateData("update" /* Update */, payload);
    }
    /**
     * Remove node from DB
     *
     * @param item
     */
    removeNode(item) {
        this._database.removeItem(item.original);
        const payload = {
            target: this.getFlatItemNode(item.original),
        };
        this._database.updateData("remove" /* Remove */, payload);
    }
    /**
     * Do reorder for target
     *
     * @param target
     */
    updateSort(target) {
        this._database.updateSort(target);
        const payload = {
            node: target,
        };
        this._database.updateData("manual_reorder" /* ManualReorder */, payload);
    }
    /**
     * Disabled reorder and block tree
     */
    lockTree() {
        this.disableReorder();
        this.blocked = true;
        this._cd.markForCheck();
    }
    /**
     * Enable reorder back and unlock tree
     */
    unlockTree() {
        this.enableReorder();
        this.blocked = false;
        this._cd.markForCheck();
    }
    updateNodesClasses() {
        this._updateClasses$.next();
    }
    getNodes(rootNode) {
        if (rootNode) {
            const rootNodeItem = this.getItemNode(rootNode);
            const children = rootNodeItem.children
                .map((nodeItem) => {
                return this.getFlatItemNode(nodeItem);
            });
            return children;
        }
        return this.treeControl.dataNodes
            .filter((nodeItem) => {
            return !nodeItem.parent;
        });
    }
    _selectNode(node) {
        const descendants = this.treeControl.getDescendants(node);
        this.checklistSelection.select(node, ...descendants);
        this.checkAllParentsSelection(node);
    }
    _deselectNode(node) {
        const descendants = this.treeControl.getDescendants(node);
        this.checklistSelection.deselect(node, ...descendants);
        this.checkAllParentsSelection(node);
    }
    _subscribeToDataChange() {
        this._database.dataChange
            .pipe(takeUntil(this._destroy$))
            .subscribe((event) => {
            this.dataSource.data = [];
            this.dataSource.data = this._database.data;
            if (this.config.change) {
                this.config.change(event);
            }
            if (event.type === "init" /* Init */ && this.config.init) {
                this.config.init(event.payload);
            }
            if (event.type === "insert" /* Insert */ && this.config.changeInsert) {
                this.config.changeInsert(event.payload);
            }
            if (event.type === "remove" /* Remove */ && this.config.changeRemove) {
                this.config.changeRemove(event.payload);
            }
            if (event.type === "update" /* Update */ && this.config.changeUpdate) {
                this.config.changeUpdate(event.payload);
            }
            if (event.type === "reorder" /* Reorder */ && this.config.changeReorder) {
                this.config.changeReorder(event.payload);
            }
        });
    }
    _emitSelectionChange() {
        if (this.config.selection.change) {
            const selected = this.checklistSelection.selected
                .map((node) => {
                return this._database.flatNodeMap.get(node);
            });
            this.config.selection.change(selected);
        }
    }
    unselectAll() {
        this.checklistSelection.clear();
        this._updateSelected();
    }
    _updateSelected() {
        var _a;
        if ((_a = this.config.selection) === null || _a === void 0 ? void 0 : _a.selected) {
            this.treeControl.dataNodes
                .filter((node) => this.config.selection.selected(node.original))
                .forEach((node) => {
                this._selectNode(node);
            });
        }
    }
    _updateExpanded() {
        if (this.config.expandLevel) {
            this.treeControl.dataNodes
                .filter((node) => this.config.expandLevel === Infinity || this.config.expandLevel > node.level)
                .forEach((node) => {
                node.expand();
            });
        }
    }
}
FsTreeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeService, deps: [{ token: FsTreeDatabaseService }, { token: i0.ChangeDetectorRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable });
FsTreeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: FsTreeDatabaseService }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }]; } });

function isGroupItem(item) {
    return item.items !== void 0;
}

class ActionItem {
    constructor(data = {}) {
        this._visible = true;
        this._isGroup = false;
        this.label = data.label || null;
        if (isGroupItem(data)) {
            this._initGroupItem(data);
        }
        else {
            this._initActionItem(data);
        }
    }
    get isGroup() {
        return this._isGroup;
    }
    get visible() {
        return this._visible;
    }
    get routerLink() {
        return this._routerLink;
    }
    get children() {
        return this._children;
    }
    click(node) {
        this._clickFn(node);
    }
    update(node) {
        this.updateVisibility(node);
        this.updateLink(node);
    }
    updateVisibility(node) {
        if (this.isGroup) {
            this._children.forEach((action) => {
                action.updateVisibility(node);
            });
            this._visible = this._children.some((action) => action._visible);
        }
        else if (this._showFn) {
            this._visible = this._showFn(node);
        }
    }
    updateLink(row) {
        if (!this._visible) {
            return;
        }
        if (this.isGroup) {
            this._children.forEach((action) => {
                action.updateLink(row);
            });
        }
        else if (this._linkFn) {
            this._routerLink = this._linkFn(row);
        }
    }
    _initActionItem(data) {
        if (data.show) {
            this._showFn = data.show;
        }
        if (data.click) {
            this._clickFn = data.click;
        }
        if (data.link) {
            this._linkFn = data.link;
        }
    }
    _initGroupItem(data) {
        this._isGroup = true;
        if (Array.isArray(data.items)) {
            this._children = data.items.map((item) => new ActionItem(item));
        }
    }
}

var TreeActionType;
(function (TreeActionType) {
    TreeActionType[TreeActionType["Menu"] = 0] = "Menu";
})(TreeActionType || (TreeActionType = {}));
class Action {
    constructor(data = {}) {
        this.type = data.type;
        this.icon = data.icon || null;
        this.items = data.items
            ? data.items.map((item) => new ActionItem(item))
            : [];
    }
    static create(data, node) {
        const action = new Action(data);
        if (action.type === TreeActionType.Menu) {
            action.items.forEach((item) => item.update(node));
        }
        return action;
    }
}

class FsNodeActionsComponent {
    constructor(_differs) {
        this._differs = _differs;
        this.actions = [];
        this._nodeDiffer = _differs.find({}).create();
    }
    ngOnInit() {
        if (this.rawActions) {
            this.actions = this.rawActions
                .map((action) => Action.create(action, this.node));
        }
    }
    ngDoCheck() {
        if (this._nodeDiffer.diff(this.node.data)) {
            this.actions.forEach((action) => {
                action.items.forEach((item) => {
                    item.update(this.node);
                });
            });
        }
    }
}
FsNodeActionsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNodeActionsComponent, deps: [{ token: i0.KeyValueDiffers }], target: i0.ɵɵFactoryTarget.Component });
FsNodeActionsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsNodeActionsComponent, selector: "fs-node-actions", inputs: { rawActions: "rawActions", node: "node" }, ngImport: i0, template: "<ng-container *ngFor=\"let action of actions\" [ngSwitch]=\"action.type\">\n  <!-- Menu -->\n  <ng-container *ngSwitchCase=\"0\">\n    <fs-menu>\n      <ng-container *ngFor=\"let item of action.items\">\n        <ng-container *ngIf=\"item.isGroup\">\n          <fs-menu-group [label]=\"item.label\">\n            <ng-container *ngFor=\"let subItem of item.children\">\n              <ng-template fs-menu-item\n                           (click)=\"subItem.click(node)\"\n                           [link]=\"subItem.routerLink?.link\"\n                           [queryParams]=\"subItem.routerLink?.queryParams\"\n                           [hidden]=\"!subItem.visible\">\n                {{ subItem.label }}\n              </ng-template>\n            </ng-container>\n          </fs-menu-group>\n        </ng-container>\n        <ng-container *ngIf=\"!item.isGroup\">\n          <ng-template fs-menu-item\n                       (click)=\"item.click(node)\"\n                       [link]=\"item.routerLink?.link\"\n                       [queryParams]=\"item.routerLink?.queryParams\"\n                       [hidden]=\"!item.visible\">\n            {{ item.label }}\n          </ng-template>\n        </ng-container>\n      </ng-container>\n    </fs-menu>\n  </ng-container>\n  <!-- /Menu -->\n</ng-container>\n", components: [{ type: i1.FsMenuComponent, selector: "fs-menu", inputs: ["class", "buttonClass"], outputs: ["opened", "closed"] }], directives: [{ type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { type: i2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1.FsMenuItemDirective, selector: "fs-menu-group,[fs-menu-item]", inputs: ["fsClass", "class", "id", "label", "hidden", "groupHidden", "dismissAfterClick", "link", "target", "queryParams"], outputs: ["click"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNodeActionsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-node-actions',
                    templateUrl: './node-actions.component.html',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.KeyValueDiffers }]; }, propDecorators: { rawActions: [{
                type: Input
            }], node: [{
                type: Input
            }] } });

class FsTreeNodeClassDirective {
    constructor(_node, _tree, _renderer, _el) {
        this._node = _node;
        this._tree = _tree;
        this._renderer = _renderer;
        this._el = _el;
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        if (this._node && this._tree.config.nodeClass) {
            this._updateClasses();
            this._listenClassesUpdateRequest();
        }
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    _listenClassesUpdateRequest() {
        this._tree.updateClasses$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._updateClasses();
        });
    }
    _updateClasses() {
        let classesList = this._tree.config.nodeClass(this._node.data);
        if (!Array.isArray(classesList)) {
            classesList = classesList.split(' ');
        }
        if (this._classesListsAreSame(this._classesList, classesList)) {
            return;
        }
        requestAnimationFrame(() => {
            if (this._classesList) {
                this._el.nativeElement.classList.remove(...this._classesList);
            }
            this._el.nativeElement.classList.add(...classesList);
            this._classesList = classesList;
        });
    }
    _classesListsAreSame(clsList1, clsList2) {
        if ((clsList1 === null || clsList1 === void 0 ? void 0 : clsList1.length) !== (clsList2 === null || clsList2 === void 0 ? void 0 : clsList2.length)) {
            return false;
        }
        return !clsList1.every((cls, index) => {
            return cls === clsList2[index];
        });
    }
}
FsTreeNodeClassDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeNodeClassDirective, deps: [{ token: i2$1.MatTreeNode }, { token: FsTreeService }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FsTreeNodeClassDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.16", type: FsTreeNodeClassDirective, selector: "[fsTreeNodeClass]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeNodeClassDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsTreeNodeClass]',
                }]
        }], ctorParameters: function () { return [{ type: i2$1.MatTreeNode }, { type: FsTreeService }, { type: i0.Renderer2 }, { type: i0.ElementRef }]; } });

function lower(array, key, get, isSame = true) {
    let lo = 0;
    let hi = array.length;
    let mid;
    while (lo < hi) {
        mid = (lo + hi) >> 1;
        if ((isSame && key <= get(array[mid]))
            || (!isSame && key < get(array[mid]))) {
            hi = mid;
        }
        else {
            lo = mid + 1;
        }
    }
    return lo;
}
function lookupNearPoint(array, target, getFn) {
    let lo = 0;
    let hi = array.length;
    let mid;
    while (lo < hi) {
        mid = (lo + hi) >> 1;
        const leftEl = array[mid - 1];
        const midEl = array[mid];
        const rightEl = array[mid + 1];
        let dL = Infinity;
        let dR = Infinity;
        let dM = Infinity;
        if (leftEl) {
            dL = Math.abs(target - getFn(leftEl));
        }
        dM = Math.abs(target - getFn(midEl));
        if (rightEl) {
            dR = Math.abs(target - getFn(rightEl));
        }
        if (dL <= dM && dM <= dR) {
            hi = mid - 1;
        }
        else if (dR <= dM && dM <= dL) {
            lo = mid + 1;
        }
        else {
            hi = mid;
        }
    }
    return lo;
}

class Droppable {
    /**
     * Here is the main Drag&Drop controller
     * Droppable class gives us higlight for drag & drop
     * Do calculation of target drop element, position (above, below, center) and level
     * Do check for canDrop rule
     *
     * @param _node
     * @param _targetEl
     * @param _draggableEl
     * @param _nodes
     * @param _dragDims
     * @param _expandNode$
     * @param _canDrop
     * @param _logger
     */
    constructor(_node, _targetEl, _draggableEl, _nodes, _dragDims, _expandNode$, _canDrop = null, _logger) {
        this._node = _node;
        this._targetEl = _targetEl;
        this._draggableEl = _draggableEl;
        this._nodes = _nodes;
        this._dragDims = _dragDims;
        this._expandNode$ = _expandNode$;
        this._canDrop = _canDrop;
        this._logger = _logger;
        // Nodes ordered by asc by their X coord
        this._orderedNodes = [];
        this._defaultDropAreaWidth = 0;
        // Level for drop (padding from left side)
        this._dropLevel = 0;
        this._canDropHere = true;
        // Х position for first level (0)
        this._rootLevelPosition = null;
        // Shift for align element under correct cursor position
        this._shiftX = 0;
        // During very fast drag we should use some array/set for contain
        // elements which had been marked "over" (center). And we will clean all of them when it's needed
        this._cacheOfDragOveredElements = new Set();
        // Default for one level. For example if element has level 4 then padding will be 4 * 40
        this._defaultPadding = 40;
        // Extra padding for correct align highlight area under element
        this._extraPadding = 30;
        // Half of height of highlight area
        this._droppableHalfHeight = 5;
        // Time for expand node when position 'center'
        this._timer$ = timer(300);
        // Cancel timer
        this._timerDestroy$ = new Subject();
        this._timerStarted = false;
        this.orderNodesByCoords();
        this._initDroppableElement();
        this._getRootPosition(this._node);
    }
    get dropPosition() {
        return this._dropPosition;
    }
    get dropTarget() {
        return this._dropTarget;
    }
    get canDropHere() {
        return this._canDropHere;
    }
    shift(val) {
        this._shiftX = val;
    }
    /**
     * Move droppable area and set dropTarget, dropLevel & dropPosition
     *
     * @param event
     */
    moveDroppable(event) {
        // Element under our drag
        const [element, isec] = this._lookupElementUnder(event);
        // Correct X position
        const eventX = (event.x || event.clientX) - this._shiftX;
        this._dropPosition = this._getPositionByIsec(isec);
        // console.log(element, this._dropPosition);
        switch (this._dropPosition) {
            case 'above':
                {
                    this.dropAbove(element, eventX);
                }
                break;
            case 'center':
                {
                    this.dropCenter(element);
                }
                break;
            case 'below':
                {
                    this.dropBelow(element, eventX);
                }
                break;
        }
    }
    dropCenter(element) {
        if (!this._timerStarted && element.node !== this._node) {
            this._startExpandTimer(element.node);
        }
        this._dropTarget = element.node;
        this.hide();
        this._canDropHere = this._checkIfCanDrop(element, this._dropTarget);
        if (this.canDropHere) {
            // Add marked element for unmark in feature
            this._cacheOfDragOveredElements.add(element.node.el);
            // Mark element
            element.node.el.classList.add('drag-over');
            this._draggableEl.classList.remove('no-drop');
        }
        else {
            this._draggableEl.classList.add('no-drop');
        }
    }
    dropBelow(element, eventX) {
        var _a;
        this._cancelExpandTimer();
        this._cancelDragOverSelections();
        // Show droppable area and move it
        // if (element.node === this._node) {
        //   this._droppableEl.style.display = 'none';
        //   return;
        // }
        this.show();
        const top = element.dimentions.top + element.dimentions.height - this._droppableHalfHeight;
        this._droppableEl.style.top = `${top}px`;
        this._updateLevelPaddingBelow(element, eventX);
        // console.log('below, ', this._dropLevel, element.node.level, element.node.data);
        // If target level for drop less than element under our drag
        if (this._dropLevel < element.node.level) {
            this._dropTarget = this._lookupNodeWithLevelBelow(element);
            this._dropPosition = 'above';
            // Когда внизу
            if (!this._dropTarget) {
                this._dropTarget = this._lookupNodeWithLevelAbove(element);
                this._dropPosition = 'below';
            }
        }
        else if (this._dropLevel > element.node.level) {
            this._dropTarget = this._lookupNodeWithLevelBelow(element);
            this._dropPosition = 'above';
        }
        else {
            this._dropTarget = element.node;
        }
        // Hide drop area if can't drop
        this._canDropHere = this._checkIfCanDrop(element, (_a = this._dropTarget) === null || _a === void 0 ? void 0 : _a.parent);
        const nextNode = this._getNodeBelow(element);
        if (!this._canDropHere || nextNode === this._node) {
            this.hide();
            this._draggableEl.classList.add('no-drop');
        }
        else {
            this._draggableEl.classList.remove('no-drop');
        }
    }
    dropAbove(element, eventX) {
        var _a;
        this._cancelExpandTimer();
        this._cancelDragOverSelections();
        // Show droppable area and move it
        // if (element.node === this._node) {
        //   this._droppableEl.style.display = 'none';
        //   return;
        // }
        this.show();
        const top = element.dimentions.top - this._droppableHalfHeight;
        this._droppableEl.style.top = `${top}px`;
        this._updateLevelPaddingAbove(element, eventX);
        // console.log('above, ', this._dropLevel, element.node.level, element.node.data);
        // If target level for drop more than element under our drag
        if (this._dropLevel > element.node.level) {
            this._dropTarget = this._lookupNodeWithLevelAbove(element);
            this._dropPosition = 'below';
        }
        else {
            this._dropTarget = element.node;
        }
        // Hide drop area if can't drop
        this._canDropHere = this._checkIfCanDrop(element, (_a = this._dropTarget) === null || _a === void 0 ? void 0 : _a.parent);
        const prevNode = this._getNodeAbove(element);
        if (!this.canDropHere || prevNode === this._node) {
            this.hide();
            this._draggableEl.classList.add('no-drop');
        }
        else {
            this._draggableEl.classList.remove('no-drop');
        }
    }
    /**
     * Strong order visible nodes by X coord
     */
    orderNodesByCoords() {
        this._logger.log('ORDER', 'order started');
        this._orderedNodes = [];
        this._nodes.forEach((node) => {
            if (node.el && !node.hidden) {
                const dimentions = node.el.getBoundingClientRect();
                const insertIndex = lower(this._orderedNodes, dimentions.y, (item) => item.dimentions.y);
                this._orderedNodes.splice(insertIndex, 0, { dimentions, node });
            }
        });
        this._logger.log('ORDER', [this._orderedNodes, this._nodes]);
    }
    /**
     * Show droppable element
     */
    show() {
        this._droppableEl.style.display = 'block';
    }
    /**
     * Hide droppable element
     */
    hide() {
        this._droppableEl.style.display = 'none';
    }
    destroy() {
        this._orderedNodes = [];
        this._cancelDragOverSelections();
        this._droppableEl.remove();
        this._cancelExpandTimer();
        this._timerDestroy$.complete();
    }
    /**
     * Return level for drop based on current X coordinate
     *
     * @param x
     * @param minLevel
     * @param maxLevel
     */
    _getDropLevel(x, minLevel, maxLevel) {
        const diff = x - this._rootLevelPosition - 20;
        if (diff >= 0) {
            const level = Math.round(diff / 40);
            if (level > maxLevel) {
                return maxLevel;
            }
            else if (level < minLevel) {
                return minLevel;
            }
            return level;
        }
        return minLevel || 0;
    }
    /**
     * Init droppable element
     */
    _initDroppableElement() {
        const droppableEl = document.createElement('div');
        const dimensions = this._targetEl.nativeElement.getBoundingClientRect();
        droppableEl.style.display = 'none';
        droppableEl.style.width = `${dimensions.width}px`;
        droppableEl.classList.add('droppable-area');
        this._defaultDropAreaWidth = parseFloat(droppableEl.style.width);
        this._targetEl.nativeElement.append(droppableEl);
        this._droppableEl = droppableEl;
    }
    /**
     * Return X coordinate for parent with level 0
     *
     * @param node
     */
    _getRootPosition(node) {
        if (!node.parent) {
            const dimensions = node.el.getBoundingClientRect();
            this._rootLevelPosition = dimensions.x;
            return;
        }
        this._getRootPosition(node.parent);
    }
    /**
     * Looking by ordered elements element which under draggable
     *
     * @param event
     */
    _lookupElementUnder(event) {
        const y = event.y || event.clientY;
        const topOffset = y;
        const halfOfDragHeight = this._dragDims.height / 2;
        const orderedNodes = this._orderedNodes
            .filter((orderedNode) => !orderedNode.node.parent || orderedNode.node.parent.isExpanded());
        const elIndex = lookupNearPoint(orderedNodes, topOffset, (node) => node.dimentions.y + halfOfDragHeight);
        const el = orderedNodes[elIndex];
        if (el) {
            const draggableRect = {
                y1: y,
                y2: y + this._dragDims.height,
            };
            const rectUnder = {
                y1: el.dimentions.y,
                y2: el.dimentions.y + el.dimentions.height,
            };
            const isec = this._calcRectIntersection(rectUnder.y1, rectUnder.y2, draggableRect.y1, draggableRect.y2);
            return [el, isec];
        }
        return null;
    }
    /**
     * Calculate intersection of rectangles
     *
     * @param y11
     * @param y12
     * @param y21
     * @param y22
     */
    _calcRectIntersection(y11, y12, y21, y22) {
        if (y21 > y12) {
            return 0;
        }
        if (y22 < y12) {
            return 1;
        }
        const y1Isec = Math.max(y11, y21);
        const y2Isec = Math.min(y12, y22);
        const intersectionArea = y2Isec - y1Isec;
        const rect1Area = y12 - y11;
        return intersectionArea / rect1Area;
    }
    /**
     * Return drop position based on persentage of overlap
     *
     * @param isec
     */
    _getPositionByIsec(isec) {
        if (isec < 0.30) {
            return 'below';
        }
        else if (isec > 0.70) {
            return 'above';
        }
        return 'center';
    }
    /**
     * Remove classes for elements which were under our drag
     */
    _cancelDragOverSelections() {
        this._cacheOfDragOveredElements.forEach((element) => {
            element.classList.remove('drag-over');
            this._cacheOfDragOveredElements.delete(element);
        });
    }
    /**
     * Update padding for drop area when position is above
     *
     * @param currentEl
     * @param eventX
     */
    _updateLevelPaddingAbove(currentEl, eventX) {
        const elIndex = this._orderedNodes.indexOf(currentEl);
        const prevEl = this._orderedNodes[elIndex - 1];
        const currentLevel = currentEl.node.level;
        const prevLevel = prevEl && prevEl.node.level;
        this._dropLevel = prevEl && prevLevel > currentLevel ? this._getDropLevel(eventX, currentLevel, prevLevel) : this._getDropLevel(eventX, currentLevel, currentLevel);
        const padding = this._rootLevelPosition + this._extraPadding + (this._dropLevel * this._defaultPadding);
        this._droppableEl.style.left = `${padding}px`;
    }
    /**
     * Update padding for drop area when position is below
     *
     * @param currentEl
     * @param eventX
     */
    _updateLevelPaddingBelow(currentEl, eventX) {
        const elIndex = this._orderedNodes.indexOf(currentEl);
        const nextEl = this._orderedNodes[elIndex + 1];
        const currentLevel = currentEl.node.level;
        const nextLevel = nextEl && nextEl.node.level;
        this._dropLevel = nextEl && currentLevel > nextLevel ? this._getDropLevel(eventX, nextLevel, currentLevel) : this._getDropLevel(eventX, nextLevel, nextLevel || currentLevel);
        // console.log('drop below: ', this._dropLevel, prevLevel, nextLevel);
        const padding = this._extraPadding + (this._dropLevel * this._defaultPadding);
        const leftPosition = this._rootLevelPosition + padding;
        const width = this._defaultDropAreaWidth - this._extraPadding;
        this._droppableEl.style.width = `${width}px`;
        this._droppableEl.style.left = `${leftPosition}px`;
    }
    /**
     * Lookup above now with same level as target
     *
     * @param element
     */
    _lookupNodeWithLevelAbove(element) {
        let dropTarget = null;
        const elementIndex = this._orderedNodes.indexOf(element);
        for (let i = elementIndex; i >= 0; --i) {
            if (this._orderedNodes[i].node.level === this._dropLevel) {
                dropTarget = this._orderedNodes[i].node;
                break;
            }
        }
        return dropTarget;
    }
    /**
     * Lookup below now with same level as target
     *
     * @param element
     */
    _lookupNodeWithLevelBelow(element) {
        let dropTarget = null;
        const elementIndex = this._orderedNodes.indexOf(element);
        for (let i = elementIndex; i < this._orderedNodes.length; ++i) {
            if (this._orderedNodes[i].node.level === this._dropLevel) {
                dropTarget = this._orderedNodes[i].node;
                break;
            }
        }
        return dropTarget;
    }
    _getNodeAbove(element) {
        const elIndex = this._orderedNodes.indexOf(element);
        const el = this._orderedNodes[elIndex];
        const prevEl = this._orderedNodes[elIndex - 1];
        if (!el || !prevEl || el.node.level !== prevEl.node.level) {
            return null;
        }
        return prevEl.node;
    }
    _getNodeBelow(element) {
        const elIndex = this._orderedNodes.indexOf(element);
        const el = this._orderedNodes[elIndex];
        const nextEl = this._orderedNodes[elIndex + 1];
        if (!el || !nextEl || el.node.level !== nextEl.node.level) {
            return null;
        }
        return nextEl.node;
    }
    /**
     * If can drop function passed - do call for result
     */
    _checkIfCanDrop(element, toParent) {
        if (this._dropIntoItSelf(toParent)) {
            return false;
        }
        if (!this._canDrop) {
            return true;
        }
        // Lookup prev and next elements at the same level
        let prevElem = null;
        let nextElem = null;
        // Index of target element (over)
        const targetIndex = this._orderedNodes.indexOf(element);
        if (this._dropPosition === 'above') {
            const el = this._orderedNodes[targetIndex - 1];
            if (el && el.node !== this._node) {
                const sameLevelWithTarget = el.node.level === this._dropTarget.level;
                if (sameLevelWithTarget) {
                    prevElem = el.node;
                }
            }
            nextElem = this._dropTarget;
        }
        else if (this._dropPosition === 'below') {
            const el = this._orderedNodes[targetIndex + 1];
            if (el && el.node !== this._node) {
                const sameLevelWithTarget = el.node.level === this._dropTarget.level;
                if (sameLevelWithTarget) {
                    nextElem = el.node;
                }
            }
            prevElem = this._dropTarget;
        }
        return this._canDrop(this._node, this._node.parent, toParent, this._dropPosition, prevElem, nextElem);
    }
    _dropIntoItSelf(targetParent) {
        const sameNode = targetParent === this._node;
        let childOfDraggableNode = false;
        if (!sameNode) {
            let parent = targetParent === null || targetParent === void 0 ? void 0 : targetParent.parent;
            while (parent && !childOfDraggableNode) {
                if (this._node === parent) {
                    childOfDraggableNode = true;
                }
                parent = parent === null || parent === void 0 ? void 0 : parent.parent;
            }
        }
        return sameNode || childOfDraggableNode;
    }
    /**
     * Starts timer for expand current node
     *
     * @param node
     */
    _startExpandTimer(node) {
        this._timerStarted = true;
        this._timer$
            .pipe(takeUntil(this._timerDestroy$))
            .subscribe(() => {
            this._expandNode$.next(node);
        });
    }
    /**
     * Cancel timer for expand
     */
    _cancelExpandTimer() {
        this._timerDestroy$.next();
        this._timerStarted = false;
    }
}

class Draggable {
    /**
     * Class for control mouse/touch drag
     *
     * @param _containerElement
     * @param _node
     * @param _el
     * @param _target
     * @param _nodes
     * @param _restrictions
     * @param _logger
     * @param _dragAxis
     */
    constructor(_containerElement, _node, _el, _target, _nodes, _restrictions, _logger, _dragAxis) {
        this._containerElement = _containerElement;
        this._node = _node;
        this._el = _el;
        this._target = _target;
        this._nodes = _nodes;
        this._restrictions = _restrictions;
        this._logger = _logger;
        this._dragAxis = _dragAxis;
        this.dragging = false;
        this._scrolled = false;
        // Drag
        // Draggable target from event.target when drag started
        this._dragTarget = null;
        // Draggable HTML node (copy from dragTarget)
        this._draggableEl = null;
        this._expandedBeforeDrag = false;
        // Observables
        this._dragStart$ = new Subject();
        this._dragEnd$ = new Subject();
        this._expandNode$ = new Subject();
        this._destroy$ = new Subject();
        this._containerHeight = 0;
        this._screenHeight = 0;
        this._limitToScroll = 0;
        // Handlers for remove listeners in feature
        this._dragStartHandler = this.dragStart.bind(this);
        this._moveHandler = this.dragTo.bind(this);
        this._dropHandler = this.dragEnd.bind(this);
        this._escapeHandler = (event) => {
            if (event.key === 'Escape') {
                this.destroyDraggable();
                this.destroyDroppable();
            }
        };
        // Listen mouse or touch events
        this._target.nativeElement.addEventListener('mousedown', this._dragStartHandler);
        this._target.nativeElement.addEventListener('touchstart', this._dragStartHandler);
        this._subscribe();
    }
    get dragStart$() {
        return this._dragStart$.pipe(takeUntil(this._destroy$));
    }
    get dragEnd$() {
        return this._dragEnd$.pipe(takeUntil(this._destroy$));
    }
    get expandNode$() {
        return this._expandNode$.pipe(takeUntil(this._destroy$));
    }
    /**
     * Prepare draggable elements and add events
     *
     * @param event
     */
    dragStart(event) {
        if (!this._node.canDrag || this.dragging) {
            // If already dragging cancel event
            if (this.dragging) {
                event.preventDefault();
                event.stopPropagation();
            }
            return;
        }
        this._touchFix(event);
        this._calcAutoScrollParams();
        // Emit event that drag started
        this._dragStart$.next();
        // Store information about expand status. If node has been expanded, then we should expand it after drag
        // this._expandedBeforeDrag = this._node.isExpanded();
        // Collapse node before drag
        // if (this._expandedBeforeDrag) {
        //   // this._node.collapse();
        //   // this._hideChildrenNodes(this._node);
        // }
        this._visibleChildren = this._getVisibleChildren(this._node);
        this._addClassForChildrenNodes();
        //this._logger.timeStart('DRAG_START');
        // Update level 0 statuses
        this._nodes.forEach((node) => {
            if (node.level === 0) {
                //this._logger.log('Drag Start Checking', node);
                node.hidden = false;
                if (node.isExpanded()) {
                    this._checkChildrenExpandedStatus(node, true);
                }
                else {
                    // this._hideChildrenNodes(node);
                }
            }
        });
        //this._logger.timeStop('DRAG_START');
        window.document.body.classList.add('fs-tree-dragging');
        this.dragging = true;
        this._dragTarget = event.target;
        this._dragDims = this._el.nativeElement.getBoundingClientRect();
        this._shiftX = event.clientX - this._dragDims.left;
        this._initDraggableElement(event);
        this._initDroppable(event);
        this._addEventListeners();
        this._dragStart$.next();
    }
    /**
     * Move draggable elements and swap items
     *
     * @param event
     */
    dragTo(event) {
        this._touchFix(event);
        const topOffset = (event.y || event.clientY) - (this._dragDims.height / 2);
        const leftOffset = (event.x || event.pageX) - this._shiftX;
        this._draggableEl.style.top = `${topOffset}px`;
        if (this._dragAxis === TreeDragAxis.XY) {
            this._draggableEl.style.left = `${leftOffset}px`;
        }
        if (event.clientY < this._limitToScroll) {
            this._scrolled = true;
            window.scrollTo(event.clientX, window.pageYOffset - 2);
            this.destroyDroppable();
            return;
        }
        if (this._screenHeight - this._limitToScroll < event.clientY) {
            this._scrolled = true;
            window.scrollTo(event.clientX, window.pageYOffset + 2);
            this.destroyDroppable();
            return;
        }
        if (this._scrolled) {
            this._initDroppable(event);
            this._scrolled = false;
        }
        this._droppable.moveDroppable(event);
    }
    /**
     * Remove events and classes after drag finish
     */
    dragEnd() {
        if (!this.dragging) {
            return;
        }
        this.destroyDraggable();
        if (this._expandedBeforeDrag) {
            this._expandedBeforeDrag = false;
            this._node.expand();
        }
        if (this._droppable &&
            this._droppable.dropTarget &&
            this._droppable.dropPosition &&
            this._droppable.canDropHere) {
            this._dragEnd$.next({
                node: this._node,
                dropInto: this._droppable.dropTarget,
                dropPosition: this._droppable.dropPosition,
            });
        }
        this.destroyDroppable();
    }
    destroyDraggable() {
        var _a;
        this.dragging = false;
        window.document.removeEventListener('mousemove', this._moveHandler);
        window.document.removeEventListener('touchmove', this._moveHandler);
        window.document.removeEventListener('mouseup', this._dropHandler);
        window.document.removeEventListener('touchend', this._dropHandler);
        window.document.removeEventListener('touchcancel', this._dropHandler);
        window.document.removeEventListener('keydown', this._escapeHandler);
        window.document.body.classList.remove('fs-tree-dragging');
        this._node.el.classList.remove('dragging');
        this._removeClassFromChildrenNodes();
        this._visibleChildren = [];
        (_a = this._draggableEl) === null || _a === void 0 ? void 0 : _a.remove();
        this._draggableEl = null;
    }
    /**
     * Destroy
     */
    destroy() {
        this._el.nativeElement.removeEventListener('mousedown', this._dragStartHandler);
        this._el.nativeElement.removeEventListener('touchstart', this._dragStartHandler);
        this._destroy$.next();
        this._destroy$.complete();
    }
    destroyDroppable() {
        var _a;
        (_a = this._droppable) === null || _a === void 0 ? void 0 : _a.destroy();
        this._droppable = null;
    }
    /**
     * Subscribe to events
     */
    _subscribe() {
        this._expandNode$
            .pipe(filter((node) => {
            return this._visibleChildren.indexOf(node) === -1;
        }), takeUntil(this._destroy$))
            .subscribe((node) => {
            if (this._node !== node) {
                this._logger.log('EXPANDED OVER', node);
                node.expand();
                this._logger.timeStart('EXPANDED_OVER');
                this._checkChildrenExpandedStatus(node, true);
                this._droppable.orderNodesByCoords();
                this._logger.timeStop('EXPANDED_OVER');
            }
        });
    }
    _addEventListeners() {
        window.document.addEventListener('mousemove', this._moveHandler);
        window.document.addEventListener('touchmove', this._moveHandler, { passive: false });
        window.document.addEventListener('mouseup', this._dropHandler);
        window.document.addEventListener('touchend', this._dropHandler);
        window.document.addEventListener('touchcancel', this._dropHandler);
        window.document.addEventListener('keydown', this._escapeHandler);
    }
    /**
     * Init draggable element
     *
     * @param event
     */
    _initDraggableElement(event) {
        const el = this._el.nativeElement.cloneNode(true);
        const dimentions = this._el.nativeElement.getBoundingClientRect();
        el.style.width = `${dimentions.width}px`;
        el.style.left = `${dimentions.left}px`;
        el.style.top = `${dimentions.top}px`;
        el.classList.add('draggable-item');
        this._el.nativeElement.append(el);
        this._draggableEl = el;
    }
    _initDroppable(event) {
        this._droppable = new Droppable(this._node, this._el, this._draggableEl, this._nodes, this._dragDims, this._expandNode$, this._restrictions.canDrop, this._logger);
        this._droppable.shift((event.x || event.clientX) - this._dragDims.left);
        this._node.el.classList.add('dragging');
    }
    /**
     * Do check and update hidden status for all children for target node
     *
     * @param node
     * @param showFirstLevel
     */
    _checkChildrenExpandedStatus(node, showFirstLevel = false) {
        if (node.original.children) {
            node.original.children.forEach((child) => {
                const childNode = this._nodes.get(child);
                if (showFirstLevel) {
                    childNode.hidden = false;
                }
                if (!childNode.isExpanded()) {
                    this._logger.log('Check Children[NOT EXPANDED]', node);
                    this._hideChildrenNodes(childNode);
                }
                else {
                    this._logger.log('Check Children[EXPANDED]', node);
                    this._checkChildrenExpandedStatus(childNode, true);
                }
            });
        }
    }
    // // Mark all children nodes as hidden = false
    // private _showChildrenNodes(node: FlatItemNode) {
    //   if (node.original.children) {
    //     node.original.children.forEach((child) => {
    //       const childNode = this._nodes.get(child);
    //       childNode.hidden = false;
    //       this._showChildrenNodes(childNode);
    //     });
    //   }
    // }
    /**
     * Mark all children nodes as hidden
     *
     * @param node
     */
    _hideChildrenNodes(node) {
        if (node.original.children) {
            node.original.children.forEach((child) => {
                const childNode = this._nodes.get(child);
                this._logger.log('Hide Children', [childNode.data, childNode]);
                childNode.hidden = true;
                this._hideChildrenNodes(childNode);
            });
        }
    }
    _addClassForChildrenNodes() {
        this._visibleChildren.forEach((child) => {
            child.el.classList.add('disabled-node');
        });
    }
    _removeClassFromChildrenNodes() {
        this._visibleChildren.forEach((child) => {
            child.el.classList.remove('disabled-node');
        });
    }
    _getVisibleChildren(node) {
        if (!node.original.children || !node.isExpanded()) {
            return [];
        }
        const result = [];
        const childrenNodes = node.original.children
            .map((child) => {
            return this._nodes.get(child);
        });
        result.push(...childrenNodes);
        childrenNodes
            .filter((child) => {
            return child.isExpanded();
        })
            .forEach((child) => {
            result.push(...this._getVisibleChildren(child));
        });
        return result;
    }
    _calcAutoScrollParams() {
        const containerSizes = this._containerElement.nativeElement.getBoundingClientRect();
        this._containerHeight = containerSizes.height;
        this._screenHeight = window.matchMedia('(orientation: landscape)').matches ? screen.availWidth || screen.width : screen.availHeight || screen.height;
        this._limitToScroll = this._screenHeight * 0.15;
        if (this._limitToScroll > 100) {
            this._limitToScroll = 100;
        }
    }
    /**
     * Fix background when mobile
     *
     * @param e
     */
    _touchFix(e) {
        if (!('clientX' in e) && !('clientY' in e)) {
            const touches = e.touches || e.originalEvent.touches;
            if (touches && touches.length) {
                e.clientX = touches[0].clientX;
                e.clientY = touches[0].clientY;
            }
            e.preventDefault();
        }
    }
}

class FsDraggableNodeContentDirective {
}
FsDraggableNodeContentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDraggableNodeContentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsDraggableNodeContentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.16", type: FsDraggableNodeContentDirective, selector: "[fsDraggableNodeContent]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDraggableNodeContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsDraggableNodeContent]',
                }]
        }] });

class FsDraggableNodeTargetDirective {
    constructor() {
        this.draggableTarget = true;
    }
}
FsDraggableNodeTargetDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDraggableNodeTargetDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsDraggableNodeTargetDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.16", type: FsDraggableNodeTargetDirective, selector: "[fsDraggableNodeTarget]", host: { properties: { "class.fs-tree-draggable-target": "this.draggableTarget" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDraggableNodeTargetDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsDraggableNodeTarget]',
                }]
        }], propDecorators: { draggableTarget: [{
                type: HostBinding,
                args: ['class.fs-tree-draggable-target']
            }] } });

class FsDraggableNodeDirective {
    constructor(_db, _logger, _el, _zone, _tree) {
        this._db = _db;
        this._logger = _logger;
        this._el = _el;
        this._zone = _zone;
        this._tree = _tree;
        this.dragStart = new EventEmitter();
        this.drop = new EventEmitter();
        this._destroy = new Subject();
    }
    ngOnInit() {
        if (this._tree.config.draggable) {
            this._zone.runOutsideAngular(() => {
                this._draggable = new Draggable(this._db.containerElement, this.node, this.draggableContent, this.draggableTarget, this._db.nestedNodeMap, { canDrop: this.candDrop }, this._logger, this._tree.config.dragAxis);
            });
            this._initSubscriptions();
        }
    }
    ngAfterViewInit() {
        const node = this._db.flatNodeMap.get(this.node);
        if (node) {
            const flatNode = this._db.nestedNodeMap.get(node);
            flatNode.el = this._el.nativeElement;
        }
    }
    ngOnDestroy() {
        if (this._tree.config.draggable) {
            this._draggable.destroy();
        }
        this._destroy.next();
        this._destroy.complete();
    }
    _initSubscriptions() {
        this._draggable.dragStart$
            .pipe(takeUntil(this._destroy))
            .subscribe(() => {
            this.dragStart.emit(this.node);
        });
        this._draggable.dragEnd$
            .pipe(takeUntil(this._destroy))
            .subscribe((data) => {
            this.drop.emit(data);
        });
    }
}
FsDraggableNodeDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDraggableNodeDirective, deps: [{ token: FsTreeDatabaseService }, { token: LoggerService }, { token: i0.ElementRef }, { token: i0.NgZone }, { token: FsTreeService }], target: i0.ɵɵFactoryTarget.Directive });
FsDraggableNodeDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.16", type: FsDraggableNodeDirective, selector: "[fsDraggableNode]", inputs: { node: ["fsDraggableNode", "node"], candDrop: ["fsCanDrop", "candDrop"] }, outputs: { dragStart: "dragStart", drop: "drop" }, queries: [{ propertyName: "draggableContent", first: true, predicate: FsDraggableNodeContentDirective, descendants: true, read: ElementRef, static: true }, { propertyName: "draggableTarget", first: true, predicate: FsDraggableNodeTargetDirective, descendants: true, read: ElementRef, static: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDraggableNodeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsDraggableNode]',
                }]
        }], ctorParameters: function () { return [{ type: FsTreeDatabaseService }, { type: LoggerService }, { type: i0.ElementRef }, { type: i0.NgZone }, { type: FsTreeService }]; }, propDecorators: { node: [{
                type: Input,
                args: ['fsDraggableNode']
            }], candDrop: [{
                type: Input,
                args: ['fsCanDrop']
            }], dragStart: [{
                type: Output
            }], drop: [{
                type: Output
            }], draggableContent: [{
                type: ContentChild,
                args: [FsDraggableNodeContentDirective, { read: ElementRef, static: true }]
            }], draggableTarget: [{
                type: ContentChild,
                args: [FsDraggableNodeTargetDirective, { read: ElementRef, static: true }]
            }] } });

class FsTreeComponent {
    constructor(tree, _el, _cd) {
        this.tree = tree;
        this._el = _el;
        this._cd = _cd;
        this.config = {};
        // List of actions for tree
        this.actions = [];
        // Nodes can be dragged&dropped. Draggable flag
        this.reorder = true;
        // Possibility to expand/collapse for nodes
        this.blocked = false;
        this.rootChildrenExist$ = this.tree.dataChange$.pipe(map((value) => {
            return value
                .some((node) => !!node.children && node.children.length > 0);
        }));
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        this.tree.init(this._el, this.config);
        this.actions = this.config.actions || [];
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    hasChild(_, _nodeData) {
        return _nodeData.expandable;
    }
    /**
     * Collapse nodes
     */
    collapseAll() {
        this.tree.collapseAll();
    }
    /**
     * Expand nodes
     */
    expandAll() {
        this.tree.expandAll();
    }
    /**
     * Enable drag&drop
     */
    enableReorder() {
        this.reorder = true;
        this._cd.markForCheck();
    }
    /**
     * Disable drag&drop
     */
    disableReorder() {
        this.reorder = false;
        this._cd.markForCheck();
    }
    unselectAll() {
        this.tree.unselectAll();
        this._cd.markForCheck();
    }
    /**
     * Insert node above target
     *
     * @param data
     * @param target
     */
    insertAbove(data = {}, target = null) {
        return this.tree.insertNodeAbove(data, target);
    }
    /**
     * Insert node below target
     *
     * @param data
     * @param target
     */
    insertBelow(data = {}, target = null) {
        return this.tree.insertNodeBelow(data, target);
    }
    /**
     * Insert node as child element for target node
     *
     * @param data
     * @param parent
     */
    append(data = {}, parent = null) {
        return this.tree.appendNode(data, parent);
    }
    /**
     * Update internal data for target
     *
     * @param data
     * @param target
     */
    updateNodeData(data = {}, target) {
        this.tree.updateNodeData(data, target);
    }
    /**
     * Remove node from DB
     *
     * @param item
     */
    remove(item) {
        this.tree.removeNode(item);
    }
    /**
     * Do reorder for target
     *
     * @param target
     */
    updateSort(target) {
        this.tree.updateSort(target);
    }
    /**
     * Disabled reorder and block tree
     */
    lockTree() {
        this.disableReorder();
        this.blocked = true;
        this._cd.markForCheck();
    }
    /**
     * Enable reorder back and unlock tree
     */
    unlockTree() {
        this.enableReorder();
        this.blocked = false;
        this._cd.markForCheck();
    }
    /**
     * Update classes for all nodes
     */
    updateNodesClasses() {
        this.tree.updateNodesClasses();
    }
    getData() {
        return this.tree.getData();
    }
    getNodes(rootNode) {
        return this.tree.getNodes(rootNode);
    }
    setData(data) {
        this.tree.setData(data);
    }
    nodeClick(node) {
        this.tree.nodeClick(node);
    }
}
FsTreeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeComponent, deps: [{ token: FsTreeService }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsTreeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsTreeComponent, selector: "fs-tree", inputs: { config: "config" }, providers: [FsTreeDatabaseService, LoggerService], queries: [{ propertyName: "treeNodeData", first: true, predicate: FsTreeNodeDirective, descendants: true }], viewQueries: [{ propertyName: "emptyItem", first: true, predicate: ["emptyItem"], descendants: true }], ngImport: i0, template: "<mat-tree \n    [dataSource]=\"tree.dataSource\"\n    [treeControl]=\"tree.treeControl\"\n    [ngClass]=\"{ 'blocked': blocked }\">\n  <mat-tree-node \n      *matTreeNodeDef=\"let node\"\n      matTreeNodeToggle\n      matTreeNodePadding\n      fsTreeNodeClass\n      class=\"fs-tree-level-{{node.level}}\"\n      [ngClass]=\"treeNodeData?.klass\"\n      [class.fs-tree-node-first]=\"node.templateContext?.first\"\n      [class.fs-tree-node-last]=\"node.templateContext?.last\"\n      [fsDraggableNode]=\"node\"\n      [fsCanDrop]=\"tree.config.canDrop\"\n      (dragStart)=\"tree.onDragStart($event)\"\n      (drop)=\"tree.onDrop($event)\">\n    <div class=\"node\" fsDraggableNodeContent>\n      <div class=\"container\">\n        <ng-container *ngIf=\"rootChildrenExist$ | async\">\n          <button \n            mat-icon-button \n            disabled \n            type=\"button\">\n          </button>\n        </ng-container>\n        <mat-checkbox \n          class=\"checklist-leaf-node\"\n          *ngIf=\"tree.config.selection\"\n          [checked]=\"tree.checklistSelection.isSelected(node)\"\n          (change)=\"tree.todoLeafItemSelectionToggle(node)\">\n        </mat-checkbox>\n        <mat-icon\n            fsDraggableNodeTarget\n            class=\"fs-tree-draggable-handle\"\n            [ngClass]=\"{ 'draggable': tree.config.draggable, 'no-drag': !node.canDrag }\">\n          drag_handle\n        </mat-icon>\n        <div \n            class=\"content\" \n            [ngClass]=\"{ 'clickable': !!node.canNodeClick }\" \n            (click)=\"nodeClick(node)\">\n          <ng-template\n            [ngTemplateOutlet]=\"treeNodeData?.templateRef || defaultNodeTemplate\"\n            [ngTemplateOutletContext]=\"node.templateContext\">\n          </ng-template>\n        </div>\n        <fs-node-actions [rawActions]=\"actions\" [node]=\"node\">\n        </fs-node-actions>\n      </div>\n    </div>\n  </mat-tree-node>\n\n  <mat-tree-node \n      *matTreeNodeDef=\"let node; when: hasChild\"\n      matTreeNodePadding\n      fsTreeNodeClass\n      class=\"fs-tree-level-{{node.level}}\"\n      [ngClass]=\"treeNodeData?.klass\"\n      [class.fs-tree-node-first]=\"node.templateContext?.first\"\n      [class.fs-tree-node-last]=\"node.templateContext?.last\"\n      [fsDraggableNode]=\"node\"\n      [fsCanDrop]=\"tree.config.canDrop\"\n      (dragStart)=\"tree.onDragStart($event)\"\n      (drop)=\"tree.onDrop($event)\">\n    <div class=\"node\" fsDraggableNodeContent>\n      <div class=\"container\">\n        <ng-container *ngIf=\"rootChildrenExist$ | async\">\n          <button \n              mat-icon-button\n              type=\"button\"\n              class=\"fs-tree-expand-toggle-button\"\n              (click)=\"tree.toggleNode(node)\"\n              [attr.aria-label]=\"'toggle ' + node.filename\">\n            <mat-icon class=\"mat-icon-rtl-mirror\">\n              {{ tree.treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}\n            </mat-icon>\n          </button>\n        </ng-container>\n\n        <mat-checkbox\n          *ngIf=\"tree.config.selection\"\n          [checked]=\"tree.descendantsAllSelected(node)\"\n          [indeterminate]=\"tree.descendantsPartiallySelected(node)\"\n          (change)=\"tree.todoItemSelectionToggle(node)\">\n        </mat-checkbox>\n\n        <mat-icon\n            fsDraggableNodeTarget\n            class=\"fs-tree-draggable-handle\"\n            [ngClass]=\"{ 'draggable': tree.config.draggable, 'no-drag': !node.canDrag }\">\n          drag_handle\n        </mat-icon>\n\n        <div class=\"content\" [ngClass]=\"{ 'clickable': !!node.canNodeClick }\" (click)=\"nodeClick(node)\">\n          <ng-template\n            [ngTemplateOutlet]=\"treeNodeData?.templateRef || defaultNodeTemplate\"\n            [ngTemplateOutletContext]=\"node.templateContext\">\n          </ng-template>\n        </div>\n        <fs-node-actions \n          *ngIf=\"actions.length > 0\" \n          [rawActions]=\"actions\" \n          [node]=\"node\">\n        </fs-node-actions>\n      </div>\n    </div>\n  </mat-tree-node>\n</mat-tree>\n\n<ng-template #defaultNodeTemplate let-data=\"data\">\n  {{data}}\n</ng-template>\n", styles: ["::ng-deep body.fs-tree-dragging{-webkit-touch-callout:none;-webkit-user-select:none;user-select:none}:host ::ng-deep .droppable-area{display:block;position:fixed;z-index:99999;height:10px;border-radius:2px}.draggable-item .container .content{cursor:copy!important}.draggable-item fs-node-actions{display:none}.no-drop .container .content{cursor:no-drop!important}.mat-checkbox{margin-right:10px}.blocked{opacity:.5}.node{width:100%;display:flex;flex:1;align-items:center;border-radius:2px}.node .container{display:flex;align-items:center}.node .container .fs-tree-draggable-target{cursor:grab;margin-right:10px;display:none}.node .container .fs-tree-draggable-target.no-drag{cursor:default!important}.node .container .fs-tree-draggable-target.draggable{display:inline}.node .container .content{align-self:stretch;display:flex;flex-direction:row;align-items:center}.node .container .content.clickable{pointer-events:all;cursor:pointer}.mat-tree-node{overflow:visible;flex-direction:column}.mat-tree-node.disabled-node .container{opacity:.5}.mat-tree-node.dragging>.node>.container{opacity:.5}.mat-tree-node .draggable-item{position:fixed;z-index:999999;background:#fff;border-radius:4px;opacity:.95;box-shadow:0 3px 6px #00000029,0 3px 6px #0000003b;min-height:40px;padding:0 15px;margin-top:35px}.mat-tree-node .draggable-item button,.mat-tree-node .draggable-item .checklist-leaf-node{display:none}\n"], components: [{ type: i2$1.MatTree, selector: "mat-tree", exportAs: ["matTree"] }, { type: i3.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { type: i4.MatCheckbox, selector: "mat-checkbox", inputs: ["disableRipple", "color", "tabIndex", "aria-label", "aria-labelledby", "id", "labelPosition", "name", "required", "checked", "disabled", "indeterminate", "aria-describedby", "value"], outputs: ["change", "indeterminateChange"], exportAs: ["matCheckbox"] }, { type: i5.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { type: FsNodeActionsComponent, selector: "fs-node-actions", inputs: ["rawActions", "node"] }], directives: [{ type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2$1.MatTreeNodeDef, selector: "[matTreeNodeDef]", inputs: ["matTreeNodeDefWhen", "matTreeNode"] }, { type: i2$1.MatTreeNode, selector: "mat-tree-node", inputs: ["role", "disabled", "tabIndex"], exportAs: ["matTreeNode"] }, { type: i2$1.MatTreeNodeToggle, selector: "[matTreeNodeToggle]", inputs: ["matTreeNodeToggleRecursive"] }, { type: i2$1.MatTreeNodePadding, selector: "[matTreeNodePadding]", inputs: ["matTreeNodePadding", "matTreeNodePaddingIndent"] }, { type: FsTreeNodeClassDirective, selector: "[fsTreeNodeClass]" }, { type: FsDraggableNodeDirective, selector: "[fsDraggableNode]", inputs: ["fsDraggableNode", "fsCanDrop"], outputs: ["dragStart", "drop"] }, { type: FsDraggableNodeContentDirective, selector: "[fsDraggableNodeContent]" }, { type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: FsDraggableNodeTargetDirective, selector: "[fsDraggableNodeTarget]" }, { type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }], pipes: { "async": i2.AsyncPipe }, viewProviders: [FsTreeService], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-tree',
                    templateUrl: './tree.component.html',
                    styleUrls: ['./tree.component.scss'],
                    providers: [FsTreeDatabaseService, LoggerService],
                    viewProviders: [FsTreeService],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsTreeService }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { config: [{
                type: Input
            }], emptyItem: [{
                type: ViewChild,
                args: ['emptyItem']
            }], treeNodeData: [{
                type: ContentChild,
                args: [FsTreeNodeDirective]
            }] } });

class FsTreeModule {
}
FsTreeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsTreeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeModule, declarations: [FsTreeComponent,
        FsTreeNodeDirective,
        FsNodeActionsComponent,
        FsDraggableNodeDirective,
        FsDraggableNodeContentDirective,
        FsDraggableNodeTargetDirective,
        FsTreeNodeClassDirective], imports: [CommonModule,
        RouterModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTreeModule,
        FsMenuModule], exports: [FsTreeComponent,
        FsTreeNodeDirective] });
FsTreeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeModule, providers: [
    // FsComponentService,
    ], imports: [[
            CommonModule,
            RouterModule,
            MatButtonModule,
            MatCheckboxModule,
            MatFormFieldModule,
            MatIconModule,
            MatInputModule,
            MatTreeModule,
            FsMenuModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTreeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule,
                        MatButtonModule,
                        MatCheckboxModule,
                        MatFormFieldModule,
                        MatIconModule,
                        MatInputModule,
                        MatTreeModule,
                        FsMenuModule,
                    ],
                    exports: [
                        FsTreeComponent,
                        FsTreeNodeDirective,
                    ],
                    declarations: [
                        FsTreeComponent,
                        FsTreeNodeDirective,
                        FsNodeActionsComponent,
                        FsDraggableNodeDirective,
                        FsDraggableNodeContentDirective,
                        FsDraggableNodeTargetDirective,
                        FsTreeNodeClassDirective,
                    ],
                    providers: [
                    // FsComponentService,
                    ],
                }]
        }] });

/*
 * Public API Surface of fs-menu
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Action, ActionItem, FlatItemNode, FsTreeComponent, FsTreeModule, FsTreeNodeDirective, ItemNode, TreeActionType, TreeDragAxis };
